import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import FhaErrorModal from '../../components/FhaErrorModal/FhaErrorModal';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import Spinner from '../../components/Spinner/Spinner';
import View from '../View/View';
import backgroundImageUrl from '../../images/mapLoading.png';

import { Panel } from '../Panel';
import { setActiveSelectorVals } from '../../actions/selectorActions';
import { reqAppInitializer } from '../../actions/appInitializerActions';
import { receiveOperations, receiveMultipleOperations } from '../../actions/operationsActions';
import { getIdsFromHash, getAppPath } from '../../utils/url';
import { hasFHA as hasFHASelector } from '../../utils/productAssets';
import { getActiveSelectorVals } from '../../utils/selectorOptions';
import { redirectTo } from "../../utils/redirectTo";
import { queryParam } from '../../utils/queryString';
import {
  MULTI_OPERATION_SELECTION,
} from '../../constants/featureFlags';

import styles from './App.css';
import { hasFieldViewSpatialDisplay as hasFieldViewSpatialDisplaySelector } from '../../utils/fieldFeatures';

export class App extends Component {
  constructor(props) {
    super(props);
    this.alreadyListeningToOpChanges = false;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { fieldId, seasonIds, mapLayerIds } = getIdsFromHash();
    const {
      fieldId: currentFieldId,
      appInitializer: currentAppInitializer,
      reqAppInitializer: reqAppInitializerProp,
    } = this.props;

    const {
      activeMapLayers,
      appInitializer,
      layers,
      seasons,
      mosFlagValue,
      receiveOperations: receiveOperationsProp,
      receiveMultipleOperations: receiveMultipleOperationsProp,
    } = nextProps;

    const shouldRegisterOpChangeListeners = mosFlagValue !== undefined &&
      !this.alreadyListeningToOpChanges;

    if (shouldRegisterOpChangeListeners) {
      if (!mosFlagValue.value) {
        nextProps.navigationEvent?.on('change:operation', receiveOperationsProp);
      } else {
        nextProps.navigationEvent?.on('change:operations', receiveMultipleOperationsProp);
      }
      this.alreadyListeningToOpChanges = true;
    }

    // If the field ID has changed, redo all season/map layer API calls for this new field by re-initializing the app
    if (currentFieldId !== fieldId) {
      reqAppInitializerProp(fieldId, mapLayerIds, seasonIds);
      return;
    }

    const selectorIds = { mapLayerIds, seasonIds };

    if (appInitializer.hasFetched) {
      const activeLayerIds = activeMapLayers.map((layer) => layer.id);
      const activeSeasonsIds = seasons.activeSeasons.map((season) => season.id);

      const layerHasChanged = activeLayerIds.join() !== mapLayerIds.join();
      const seasonHasChanged = activeSeasonsIds.join() !== seasonIds.join();

      if (layerHasChanged || seasonHasChanged) {
        // If the URL and active map layer ID and season ID are not synced, fix it
        let newActiveMapLayers;
        let newActiveSeasons;

        if (currentAppInitializer.isFetching) { // If the app was just initialized, change the URL to the active values
          newActiveMapLayers = activeMapLayers.map((layer) => layer.id);
          newActiveSeasons = seasons.activeSeasons.map((season) => season.id);
        } else { // If app was not just initialized, update the active values; request the values in the URL
          const { list, sortedSeasonKeys } = seasons;
          const active = getActiveSelectorVals(
            list,
            sortedSeasonKeys,
            layers,
            selectorIds
          );
          newActiveMapLayers = active.activeMapLayers.map((layer) => layer.id);
          newActiveSeasons = active.activeSeasons.map((season) => season.id);

          nextProps.setActiveSelectorVals(
            fieldId,
            active.activeMapLayers,
            active.activeSeasons,
            seasons
          );
        }

        const mapLayerShouldChange = newActiveMapLayers.join() !== mapLayerIds.join();
        const seasonShouldChange = newActiveSeasons.join() !== seasonIds.join();
        const shouldRedirect = seasonShouldChange || mapLayerShouldChange;
        const clientId = queryParam('client-id');
        const clientIdUrl = clientId ? `?client-id=${clientId}` : '';

        // Update the URL with the new active season ID and map layer ID
        if (shouldRedirect) {
          redirectTo(`${getAppPath()}${fieldId}/${newActiveSeasons.join('|')}/${newActiveMapLayers.join('|')}`, false, clientIdUrl)
        }
      }
    }
  }

  render() {
    const {
      appInitializer,
      fieldHasFetched,
      fieldId,
      legendMain,
      mapLayerMain,
      panelMain,
      mapLayerSplit,
      panelSplit,
      legendSplit,
      userDetails,
      content,
      modalMain,
      modalSplit,
      hasFHA,
    } = this.props;

    const isFHARoute = window.location.hash.includes('fha');
    const showErrorModalForFHA = isFHARoute && !hasFHA;

    if (appInitializer.hasFetched && showErrorModalForFHA) {
      return <FhaErrorModal />;
    }

    if (!fieldId || appInitializer.didError) {
      return <ErrorMessage message="loadingError" />;
    }

    const backgroundImage = `url(${backgroundImageUrl})`;

    if (!fieldHasFetched || !userDetails.hasFetched) {
      const loadingMainClasses = classNames(styles.mainPage, styles.mainPageLoading);

      return (
        <div className={loadingMainClasses} style={{ backgroundImage }}>
          <Spinner />
          <Panel />
        </div>
      );
    }

    return (
      <div data-test-id="dmb-app">
        <div
          className={styles.mainPage}
          style={{ backgroundImage }}
        >
          {!appInitializer.hasFetched && <Spinner />}
          <View
            content={content}
            index={0}
            legend={legendMain}
            mapLayer={mapLayerMain}
            modal={modalMain}
            panel={panelMain}
          />
          {mapLayerSplit && (
            <View
              index={1}
              legend={legendSplit}
              mapLayer={mapLayerSplit}
              modal={modalSplit}
              panel={panelSplit}
            />
          )}
        </div>
      </div>
    );
  }
}

App.propTypes = {
  activeMapLayers: PropTypes.array.isRequired,
  appInitializer: PropTypes.object.isRequired,
  content: PropTypes.object.isRequired,
  fieldHasFetched: PropTypes.bool.isRequired,
  fieldId: PropTypes.string.isRequired,
  layers: PropTypes.object.isRequired,
  legendMain: PropTypes.object,
  legendSplit: PropTypes.object,
  mapLayerMain: PropTypes.object.isRequired,
  mapLayerSplit: PropTypes.object,
  modalMain: PropTypes.object,
  modalSplit: PropTypes.object,
  navigationEvent: PropTypes.object.isRequired,
  panelMain: PropTypes.object.isRequired,
  panelSplit: PropTypes.object,
  receiveOperations: PropTypes.func,
  receiveMultipleOperations: PropTypes.func,
  reqAppInitializer: PropTypes.func.isRequired,
  seasons: PropTypes.object.isRequired,
  setActiveSelectorVals: PropTypes.func.isRequired,
  userDetails: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  const { fieldId } = getIdsFromHash();
  const mosFlagValue = state.feature.userConfigFeatures[MULTI_OPERATION_SELECTION];

  return {
    navigationEvent: state?.app?.nav?.emitter ?? { on: () => { } },
    fieldId,
    activeMapLayers: state.layers.activeMapLayers,
    appInitializer: state.appInitializer,
    fieldHasFetched: state.field.hasFetched,
    layers: state.layers.list,
    seasons: state.seasons,
    userDetails: state.user,
    mosFlagValue,
    hasFHA: hasFHASelector(state.field?.features),
    hasFieldViewSpatialDisplayAsset: hasFieldViewSpatialDisplaySelector(state.field?.features)
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      reqAppInitializer,
      receiveOperations,
      receiveMultipleOperations,
      setActiveSelectorVals
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
